/**
 * Gizmo visual constants — colors, sizes, thresholds.
 */

import {
  COLOR_AXIS_X_HEX,
  COLOR_AXIS_Y_HEX,
  COLOR_AXIS_Z_HEX,
} from "../../ui/tokens.ts";

// ---------------------------------------------------------------------------
// Axis colors
// ---------------------------------------------------------------------------

export const AXIS_COLOR_X = COLOR_AXIS_X_HEX;
export const AXIS_COLOR_Y = COLOR_AXIS_Y_HEX;
export const AXIS_COLOR_Z = COLOR_AXIS_Z_HEX;
export const AXIS_COLOR_X_HOVER = 0xff6b5a;
export const AXIS_COLOR_Y_HOVER = 0x5bf5a0;
export const AXIS_COLOR_Z_HOVER = 0x8cc4ff;
export const CENTER_COLOR = 0xaaaaaa;
export const CENTER_COLOR_HOVER = 0xffffff;

export const AXIS_COLORS: Record<string, number> = {
  x: AXIS_COLOR_X,
  y: AXIS_COLOR_Y,
  z: AXIS_COLOR_Z,
  free: CENTER_COLOR,
};

export const AXIS_COLORS_HOVER: Record<string, number> = {
  x: AXIS_COLOR_X_HOVER,
  y: AXIS_COLOR_Y_HOVER,
  z: AXIS_COLOR_Z_HOVER,
  free: CENTER_COLOR_HOVER,
};

// ---------------------------------------------------------------------------
// Sizing
// ---------------------------------------------------------------------------

/** Desired apparent size of the gizmo in pixels (screen-space constant). */
export const GIZMO_SCREEN_PX = 110;

/** Opacity of non-hovered axes when one axis is being hovered. */
export const DIM_OPACITY = 0.3;

/** Default opacity for gizmo handles. */
export const DEFAULT_OPACITY = 0.85;

// ---------------------------------------------------------------------------
// Translate geometry dimensions (in gizmo-local units, scaled to screen size)
// ---------------------------------------------------------------------------

export const ARROW_SHAFT_RADIUS = 0.015;
export const ARROW_SHAFT_LENGTH = 0.7;
export const ARROW_HEAD_RADIUS = 0.05;
export const ARROW_HEAD_LENGTH = 0.15;
export const PLANE_QUAD_SIZE = 0.15;
export const PLANE_QUAD_OFFSET = 0.3;
export const CENTER_SPHERE_RADIUS = 0.06;

// ---------------------------------------------------------------------------
// Rotate geometry dimensions
// ---------------------------------------------------------------------------

export const RING_RADIUS = 0.85;
export const RING_TUBE = 0.025;
export const SCREEN_RING_RADIUS = 1.0;

// ---------------------------------------------------------------------------
// Scale geometry dimensions
// ---------------------------------------------------------------------------

export const SCALE_BOX_SIZE = 0.08;

// ---------------------------------------------------------------------------
// Pose mode — enlarged center for free-axis joint movement
// ---------------------------------------------------------------------------

export const POSE_CENTER_RADIUS = 0.18;
export const POSE_HIT_CENTER_RADIUS = 0.24;

// ---------------------------------------------------------------------------
// Operator gizmo — single normal-aligned handle (extrude / inset)
// ---------------------------------------------------------------------------

export const OP_HANDLE_SHAFT_RADIUS = 0.02;
export const OP_HANDLE_SHAFT_LENGTH = 0.8;
export const OP_HANDLE_HEAD_RADIUS = 0.06;
export const OP_HANDLE_HEAD_LENGTH = 0.18;
export const OP_HIT_HANDLE_RADIUS = 0.08;
export const OP_HANDLE_COLOR = 0xffcc00;
export const OP_HANDLE_COLOR_HOVER = 0xffee66;
export const SURFACE_CUT_PREVIEW_COLOR = 0xff5a2f;
export const SURFACE_CUT_PREVIEW_COLOR_HOVER = 0xff9f3a;

// ---------------------------------------------------------------------------
// Hit volume multipliers (relative to visual geometry)
// ---------------------------------------------------------------------------

export const HIT_ARROW_RADIUS = 0.06;
export const HIT_RING_TUBE = 0.14;
export const HIT_CENTER_RADIUS = 0.12;
export const HIT_PLANE_SIZE = 0.25;

// ---------------------------------------------------------------------------
// Touch-friendly handle sizes (coarse-pointer / mobile)
// ---------------------------------------------------------------------------

/** Visual sphere radius at each axis tip for touch translate handles. */
export const TOUCH_HANDLE_SPHERE_RADIUS = 0.13;
/** Hit sphere radius at axis tip for touch translate hit testing. */
export const TOUCH_HIT_SPHERE_RADIUS = 0.17;

/** Scale end-cap cube side length for touch (larger than default SCALE_BOX_SIZE = 0.08). */
export const SCALE_BOX_TOUCH_SIZE = 0.12;
/** Hit cylinder radius for touch scale handles. */
export const HIT_SCALE_TOUCH_RADIUS = 0.1;
